import { useEffect, useRef } from 'react';

/**
 * Custom hook for subtle parallax effect on scroll
 * Uses requestAnimationFrame for smooth, GPU-accelerated performance
 * @param {Object} options - Configuration options
 * @param {number} options.speed - Parallax speed (0.1 to 0.5 recommended, lower = more subtle)
 * @param {string} options.direction - 'up' (default) or 'down' for parallax direction
 * @returns {React.RefObject} - Ref to attach to the element
 */
export const useParallax = (options = {}) => {
  const elementRef = useRef(null);
  const offsetRef = useRef(0);

  useEffect(() => {
    const speed = options.speed || 0.3; // Default subtle speed
    const direction = options.direction || 'up';
    const multiplier = direction === 'down' ? 1 : -1;

    let animationId;
    let isScrolling = false;

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;

        animationId = requestAnimationFrame(() => {
          if (elementRef.current) {
            const elementRect = elementRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            // Only apply parallax when element is visible in viewport
            if (elementRect.top < windowHeight && elementRect.bottom > 0) {
              // Calculate scroll position relative to element
              const scrollProgress = (windowHeight - elementRect.top) / (windowHeight + elementRect.height);
              const movement = scrollProgress * 50 * speed * multiplier; // Max 50px movement

              // Use transform for GPU acceleration
              elementRef.current.style.transform = `translateY(${movement}px)`;
            }
          }

          isScrolling = false;
        });
      }
    };

    // Use passive scroll listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial call
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [options.speed, options.direction]);

  return elementRef;
};

/**
 * Hook for scroll-triggered animations that triggers on element visibility
 * Enhanced version with more options
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Percentage of element visible (0-1), default 0.1
 * @param {string} options.rootMargin - Margin around root, default '0px 0px -100px 0px'
 * @param {boolean} options.once - Only trigger animation once, default true
 * @returns {React.RefObject} - Ref to attach to the element
 */
export const useScrollTrigger = (options = {}) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: options.threshold !== undefined ? options.threshold : 0.1,
      rootMargin: options.rootMargin || '0px 0px -100px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add animation class when element enters viewport
          entry.target.classList.add('in-view');

          // Stop observing if only needed once
          if (options.once !== false) {
            observer.unobserve(entry.target);
          }
        } else if (!options.once) {
          // Remove animation if re-triggering is enabled
          entry.target.classList.remove('in-view');
        }
      });
    }, observerOptions);

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [options.threshold, options.rootMargin, options.once]);

  return elementRef;
};

/**
 * Hook to apply parallax effect to multiple elements
 * @param {Object} containerRef - Ref to container with multiple children
 * @param {Object} options - Configuration options
 * @returns {void}
 */
export const useParallaxContainer = (containerRef, options = {}) => {
  useEffect(() => {
    const speed = options.speed || 0.2;
    let animationId;
    let isScrolling = false;

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;

        animationId = requestAnimationFrame(() => {
          if (containerRef.current) {
            const children = containerRef.current.querySelectorAll('[data-parallax]');

            children.forEach((child) => {
              const rect = child.getBoundingClientRect();
              const windowHeight = window.innerHeight;

              if (rect.top < windowHeight && rect.bottom > 0) {
                const childSpeed = parseFloat(child.dataset.parallax) || speed;
                const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
                const movement = scrollProgress * 50 * childSpeed;

                child.style.transform = `translateY(${movement}px)`;
              }
            });
          }

          isScrolling = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [containerRef, options.speed]);
};
